import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NotificationHandlerContext } from '@interfaces/notifications/notification-handler-context';
import { CurrentUserNotificationResponse } from '@interfaces/notifications/responses/current-user-notification.response';
import { Router } from '@angular/router';
import { NotificationsHttpService } from '@services/http/notifications/notifications-http.service';
import { NotificationHandlerDispatcherService } from '@services/notifications/notification-handler-dispatcher.service';
import { NotificationPresentationService } from '@services/notifications/notification-presentation.service';
import { NotificationReadStateService } from '@services/notifications/notification-read-state.service';
import {
  applyNotificationReadState,
  countUnreadNotifications,
} from '@services/notifications/helpers/notification-read-state.utils';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { ButtonMenuComponent } from '@shared/components/button-menu/button-menu.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { NotificationReadToggleComponent } from '@shared/components/notification-read-toggle/notification-read-toggle.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { AppState } from '@src/app/state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-current-user-notifications',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PaginatorModule,
    ButtonMenuComponent,
    NotificationReadToggleComponent,
  ],
  templateUrl: './current-user-notifications.component.html',
  styleUrls: ['./current-user-notifications.component.scss'],
})
export class CurrentUserNotificationsComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('notificationTextDialog') notificationTextDialogRef: DialogComponent | null = null;

  notifications: CurrentUserNotificationResponse[] = [];
  totalRecords = 0;
  currentPage = 1;
  pageSize = 20;
  isLoadingPage = false;

  notificationTextModalTitle = '';
  notificationTextModalHtml = '';

  /** Notificação com a troca de lida/não lida em andamento (card 3552). */
  readBusyId: string | null = null;
  bulkBusy = false;
  /** Opções de "Marcar notificações", refeitas quando a lista muda. */
  bulkMenuItems: MenuItem[] = [];
  private readonly readStateDestroy$ = new Subject<void>();

  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Dashboard',
  };
  breadcrumbs: MenuItem[] = [{ label: 'Notificações' }];

  constructor(
    private readonly notificationsHttpService: NotificationsHttpService,
    private readonly notificationHandlerDispatcherService: NotificationHandlerDispatcherService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly notificationPresentation: NotificationPresentationService,
    private readonly notificationReadState: NotificationReadStateService,
    protected override translateService: TranslateService,
    protected override store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.refreshBulkMenuItems();
    this.loadNotificationsPage();

    // Mudança feita aqui, no sino ou na abertura de uma notificação: a página acompanha.
    this.notificationReadState.changes$
      .pipe(takeUntil(this.readStateDestroy$))
      .subscribe(change => {
        this.notifications = applyNotificationReadState(this.notifications, change);
        this.refreshBulkMenuItems();
        this.cdr.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    this.readStateDestroy$.next();
    this.readStateDestroy$.complete();
    super.ngOnDestroy();
  }

  /** Botão da notificação: alterna lida ↔ não lida sem abri-la. */
  setNotificationRead(notification: CurrentUserNotificationResponse, isRead: boolean): void {
    if (this.readBusyId) return;

    this.readBusyId = notification.id;
    this.notificationReadState.setRead([notification.id], isRead).subscribe(() => {
      this.readBusyId = null;
      this.cdr.markForCheck();
    });
  }

  /** Enter ou Espaço no item abre a notificação, como o clique. */
  openNotificationFromKeyboard(event: Event, notification: CurrentUserNotificationResponse): void {
    event.preventDefault();
    this.openNotification(notification);
  }

  onPageChange(event: PaginatorState): void {
    const nextPage = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    this.currentPage = nextPage;
    this.pageSize = event.rows ?? this.pageSize;
    this.loadNotificationsPage();
  }

  openNotification(notification: CurrentUserNotificationResponse): void {
    this.notificationHandlerDispatcherService.handle(
      notification,
      this.getNotificationHandlerContext()
    );
  }

  /** "Marcar notificações": todas como lidas ou todas como não lidas (card 3552). */
  markAll(isRead: boolean): void {
    if (this.bulkBusy) return;

    this.bulkBusy = true;
    this.refreshBulkMenuItems();
    this.notificationReadState.setAllRead(isRead).subscribe(() => {
      this.bulkBusy = false;
      this.refreshBulkMenuItems();
      this.cdr.markForCheck();
    });
  }

  getNotificationTitle(notification: CurrentUserNotificationResponse): string {
    return this.notificationPresentation.getTitle(notification, {
      textMessageFallbackTitle: 'Mensagem',
    });
  }

  getNotificationMessage(notification: CurrentUserNotificationResponse): string {
    return this.notificationPresentation.getMessage(notification);
  }

  closeNotificationTextDialog(): void {
    this.notificationTextDialogRef?.close();
  }

  get unreadCount(): number {
    return countUnreadNotifications(this.notifications);
  }

  /** Desabilita o lado que não tem o que mudar e as duas opções enquanto um pedido está em andamento. */
  private refreshBulkMenuItems(): void {
    const hasUnread = this.notifications.some(item => !item.isRead);
    const hasRead = this.notifications.some(item => item.isRead);

    this.bulkMenuItems = [
      {
        label: 'Marcar como lida',
        icon: 'pi pi-check',
        disabled: this.bulkBusy || !hasUnread,
        command: () => this.markAll(true),
      },
      {
        label: 'Marcar como não lida',
        icon: 'pi pi-envelope',
        disabled: this.bulkBusy || !hasRead,
        command: () => this.markAll(false),
      },
    ];
  }

  private loadNotificationsPage(): void {
    this.isLoadingPage = true;
    this.notificationsHttpService
      .getCurrentPaged({
        page: this.currentPage,
        pageSize: this.pageSize,
      })
      .pipe(
        finalize(() => {
          this.isLoadingPage = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.notifications = response?.data ?? [];
          this.totalRecords = response?.total ?? 0;
          this.currentPage = response?.currentPage ?? this.currentPage;
          this.pageSize = response?.itemsPerPage ?? this.pageSize;
          this.refreshBulkMenuItems();
          this.cdr.markForCheck();
        },
        error: () => {
          this.notifications = [];
          this.totalRecords = 0;
          this.cdr.markForCheck();
        },
      });
  }

  private getNotificationHandlerContext(): NotificationHandlerContext {
    return {
      openHtmlModal: (title, htmlContent) => {
        this.notificationTextModalTitle = title;
        this.notificationTextModalHtml = htmlContent;
        this.notificationTextDialogRef?.open(
          '',
          '',
          '',
          '',
          '',
          'notification-text-dialog-page',
          false,
          false
        );
        this.cdr.markForCheck();
      },
      navigate: (url: string) => this.router.navigateByUrl(url),
      markAsRead: notification => this.markNotificationAsReadSilently(notification),
    };
  }

  /** Abrir a notificação a marca como lida; o serviço espalha a mudança para o sino e a página. */
  private markNotificationAsReadSilently(notification: CurrentUserNotificationResponse): void {
    if (notification.isRead) return;

    this.notificationsHttpService.markAsRead([notification.id]).subscribe({
      next: () => this.notificationReadState.publishOpened(notification.id),
      error: () => {
        // silencioso
        this.cdr.markForCheck();
      },
    });
  }
}
